
import { PostgrestError } from "@supabase/supabase-js";
import { Tables } from "@/integrations/supabase/types";

/**
 * Type predicate to check if a value is a PostgrestError
 */
export function isPostgrestError(error: unknown): error is PostgrestError {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    "message" in error &&
    "details" in error
  );
}

/**
 * Helper function to format error messages from Supabase
 */
export function formatSupabaseError(error: unknown): string {
  if (isPostgrestError(error)) {
    return error.message;
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  return "An unknown error occurred";
}

/**
 * Safely cast Supabase query results to the expected type
 * This helps resolve TypeScript errors when working with Supabase data
 */
export function handleQueryResult<T>(data: unknown): T[] {
  if (!data || isPostgrestError(data)) {
    return [];
  }
  return data as unknown as T[];
}

/**
 * Safely cast a single Supabase query result to the expected type
 */
export function handleSingleQueryResult<T>(data: unknown, defaultValue: T): T {
  if (!data || isPostgrestError(data)) {
    return defaultValue;
  }
  return data as unknown as T;
}

/**
 * Type-safe helper for eq filter in Supabase queries
 */
export function eq<T extends keyof any>(column: string, value: T): { [key: string]: T } {
  return { [column]: value };
}

// Type definitions for various database tables
export type Profile = Tables<"profiles">;
export type VacationRequest = Tables<"vacation_requests">;
export type BusyPeriod = Tables<"busy_periods">;

// Default empty objects for various entity types
export const emptyProfile: Profile = {
  id: "",
  email: "",
  full_name: "",
  avatar_url: null,
  created_at: null,
  last_sign_in_at: null,
  role: null,
  vacation_days: 0,
  is_admin: false
};

export const emptyVacationRequest: VacationRequest = {
  id: "",
  user_id: null,
  start_date: "",
  end_date: "",
  is_half_day: false,
  created_at: null,
  updated_at: null,
  reason: "",
  status: "pending",
  manager_comment: null
};

export const emptyBusyPeriod: BusyPeriod = {
  id: "",
  start_date: "",
  end_date: "",
  created_by: null,
  created_at: null,
  description: "",
  team_scope: null,
  country: null
};
